import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Download, FileSpreadsheet, TrendingUp, TrendingDown, DollarSign, 
  Clock, AlertCircle, Users, Calendar, ArrowUpRight, ArrowDownRight,
  PieChart, BarChart3
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area
} from "recharts";

const COLORS = ['#2E5C46', '#4A8F76', '#78B89A', '#A6D4C3', '#D4EEE5', '#FFB347', '#FF6B6B'];

export default function ReportsPage() {
  const { token } = useAuth();
  const [viewType, setViewType] = useState("calendar");
  const [selectedYear, setSelectedYear] = useState("");
  const [periodMode, setPeriodMode] = useState("year"); // "year" | "quarter" | "month" | "custom"
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [financialOverview, setFinancialOverview] = useState(null);
  const [agingReport, setAgingReport] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [clientProfitability, setClientProfitability] = useState(null);
  const [yearComparison, setYearComparison] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState({});

  // Memoised list of overdue invoices sorted by days overdue (used by aging tab table)
  const overdueInvoicesSorted = useMemo(() => {
    if (!agingReport?.aging_buckets) return [];
    return agingReport.aging_buckets
      .filter((b) => b.bucket !== "current")
      .flatMap((b) => b.invoices || [])
      .sort((a, b) => b.days_overdue - a.days_overdue);
  }, [agingReport]);

  const headers = { Authorization: `Bearer ${token}` };

  // Compute API params based on period mode
  const getDateParams = () => {
    const yr = selectedYear || new Date().getFullYear().toString();
    if (periodMode === "custom" && customStart && customEnd) {
      return { start_date: customStart, end_date: customEnd };
    }
    if (periodMode === "quarter") {
      const qMap = { Q1: ["01-01","03-31"], Q2: ["04-01","06-30"], Q3: ["07-01","09-30"], Q4: ["10-01","12-31"] };
      const [qs, qe] = qMap[selectedQuarter] || ["01-01","03-31"];
      const baseYear = viewType === "financial"
        ? (parseInt(yr.replace("FY","")) + 1999).toString()
        : yr;
      return { start_date: `${baseYear}-${qs}`, end_date: `${baseYear}-${qe}` };
    }
    if (periodMode === "month" && selectedMonth) {
      const [y, m] = selectedMonth.split("-");
      const lastDay = new Date(parseInt(y), parseInt(m), 0).getDate();
      return { start_date: `${selectedMonth}-01`, end_date: `${selectedMonth}-${lastDay}` };
    }
    return { view_type: viewType, ...(yr && yr !== "all" ? { year: yr } : {}) };
  };

  useEffect(() => {
    fetchAllData();
  }, [viewType, selectedYear, periodMode, selectedQuarter, selectedMonth, customStart, customEnd]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const dateParams = getDateParams();
      const params = { ...dateParams };

      const [overviewRes, agingRes, cashFlowRes, clientRes, yearRes, pnlRes] = await Promise.all([
        axios.get(`${API}/analytics/financial-overview`, { headers, params }),
        axios.get(`${API}/analytics/aging-report`, { headers }),
        axios.get(`${API}/analytics/cash-flow`, { headers, params }),
        axios.get(`${API}/analytics/client-profitability`, { headers, params }),
        axios.get(`${API}/analytics/year-comparison`, { headers, params: { view_type: viewType } }),
        axios.get(`${API}/analytics/pnl`, { headers, params }),
      ]);

      setFinancialOverview(overviewRes.data);
      setAgingReport(agingRes.data);
      setCashFlow(cashFlowRes.data);
      setClientProfitability(clientRes.data);
      setYearComparison(yearRes.data);
      setPnlData(pnlRes.data);
    } catch (error) {
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const exportReport = async (type) => {
    setExportLoading(prev => ({ ...prev, [type]: true }));
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        headers,
        params: { year: selectedYear || new Date().getFullYear().toString() },
        responseType: 'blob'
      });
      downloadFile(response.data, `${type}_${selectedYear || 'all'}.xlsx`);
      toast.success("Report exported successfully");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExportLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { 
      style: 'currency', 
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const currentYear = new Date().getFullYear();
  const calendarYears = [2024, 2025, 2026, 2027];
  const financialYears = ['FY24', 'FY25', 'FY26', 'FY27'];

  if (loading) {
    return (
      <DashboardLayout title="Financial Reports">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-muted-foreground">Loading analytics...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Financial Reports">
      <div className="space-y-6" data-testid="reports-page">
        {/* Header Controls */}
        <div className="flex flex-col gap-3">
          {/* Row 1: View type + Year */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-sm">View:</Label>
                <Select value={viewType} onValueChange={setViewType}>
                  <SelectTrigger className="w-[160px]" data-testid="view-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="calendar">Calendar Year</SelectItem>
                    <SelectItem value="financial">Financial Year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {periodMode === "year" && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm">Year:</Label>
                  <Select value={selectedYear || "all"} onValueChange={(v) => setSelectedYear(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[120px]" data-testid="year-select">
                      <SelectValue placeholder="All Years" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      {(viewType === "financial" ? financialYears : calendarYears).map((year) => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Button variant="outline" onClick={() => exportReport('invoices')} disabled={exportLoading.invoices}>
              <Download className="w-4 h-4 mr-2" />
              Export All Data
            </Button>
          </div>

          {/* Row 2: Period mode selector */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-muted-foreground font-medium">Period:</span>
            {["year", "quarter", "month", "custom"].map(mode => (
              <button key={mode} onClick={() => setPeriodMode(mode)}
                data-testid={`period-mode-${mode}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all capitalize
                  ${periodMode === mode ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                {mode === "year" ? "Annual" : mode === "custom" ? "Custom Range" : mode.charAt(0).toUpperCase() + mode.slice(1) + "ly"}
              </button>
            ))}

            {/* Quarter selector */}
            {periodMode === "quarter" && (
              <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                <SelectTrigger className="w-[80px] h-8 text-xs" data-testid="quarter-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Q1">Q1</SelectItem>
                  <SelectItem value="Q2">Q2</SelectItem>
                  <SelectItem value="Q3">Q3</SelectItem>
                  <SelectItem value="Q4">Q4</SelectItem>
                </SelectContent>
              </Select>
            )}

            {/* Month selector */}
            {periodMode === "month" && (
              <Input type="month" value={selectedMonth}
                onChange={e => setSelectedMonth(e.target.value)}
                className="w-[150px] h-8 text-xs" data-testid="month-select" />
            )}

            {/* Custom date range */}
            {periodMode === "custom" && (
              <div className="flex items-center gap-2">
                <Input type="date" value={customStart}
                  onChange={e => setCustomStart(e.target.value)}
                  className="w-[140px] h-8 text-xs" data-testid="custom-start-date" />
                <span className="text-xs text-muted-foreground">–</span>
                <Input type="date" value={customEnd}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="w-[140px] h-8 text-xs" data-testid="custom-end-date" />
              </div>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        {financialOverview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Invoiced</p>
                    <p className="text-2xl font-bold text-primary">
                      {formatCurrency(financialOverview.summary.total_invoiced)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {financialOverview.summary.invoice_count} invoices
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Collected</p>
                    <p className="text-2xl font-bold text-green-600">
                      {formatCurrency(financialOverview.summary.total_paid)}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs">
                        {financialOverview.summary.collection_rate}% collected
                      </Badge>
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Outstanding</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {formatCurrency(financialOverview.summary.total_pending)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      DSO: {financialOverview.summary.days_sales_outstanding} days
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-yellow-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Invoice</p>
                    <p className="text-2xl font-bold">
                      {formatCurrency(financialOverview.summary.average_invoice_value)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Per invoice
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs for different reports */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pnl">P&amp;L</TabsTrigger>
            <TabsTrigger value="clients">By Client</TabsTrigger>
            <TabsTrigger value="aging">Aging</TabsTrigger>
            <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Monthly Revenue Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Monthly Revenue</CardTitle>
                  <CardDescription>Revenue breakdown by month</CardDescription>
                </CardHeader>
                <CardContent>
                  {financialOverview?.by_month && (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={financialOverview.by_month}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="paid" fill="#2E5C46" name="Paid" stackId="a" />
                        <Bar dataKey="pending" fill="#FFB347" name="Pending" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Revenue by Client Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Revenue by Client</CardTitle>
                  <CardDescription>Client contribution to total revenue</CardDescription>
                </CardHeader>
                <CardContent>
                  {clientProfitability?.clients && (
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={clientProfitability.clients}
                          dataKey="revenue"
                          nameKey="client"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={({ client, revenue_share }) => `${client} (${revenue_share}%)`}
                        >
                          {clientProfitability.clients.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* P&L Tab */}
          <TabsContent value="pnl" className="space-y-4" data-testid="pnl-tab">
            {pnlData && (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="card-hover">
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Revenue</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(pnlData.summary.total_revenue)}</p>
                      <p className="text-xs text-muted-foreground mt-1">{pnlData.invoice_count} invoices</p>
                    </CardContent>
                  </Card>
                  <Card className="card-hover">
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Total Expenses</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(pnlData.summary.total_expenses)}</p>
                      <p className="text-xs text-muted-foreground mt-1">Recorded + bank</p>
                    </CardContent>
                  </Card>
                  <Card className="card-hover">
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Net Profit</p>
                      <p className={`text-2xl font-bold ${pnlData.summary.net_profit >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {formatCurrency(pnlData.summary.net_profit)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{pnlData.summary.net_margin_pct}% margin</p>
                    </CardContent>
                  </Card>
                  <Card className="card-hover">
                    <CardContent className="pt-6">
                      <p className="text-sm text-muted-foreground">Gross Margin</p>
                      <p className="text-2xl font-bold">{pnlData.summary.gross_margin_pct}%</p>
                      <p className="text-xs text-muted-foreground mt-1">Gross profit ratio</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Monthly P&L bar chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Monthly P&amp;L</CardTitle>
                      <CardDescription>Revenue vs expenses by month</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={pnlData.monthly_data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Bar dataKey="revenue" fill="#2E5C46" name="Revenue" />
                          <Bar dataKey="expenses" fill="#FF6B6B" name="Expenses" />
                          <Legend />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* P&L Statement */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">P&amp;L Statement</CardTitle>
                      <CardDescription>{pnlData.period?.label}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between py-2 border-b font-semibold text-green-700">
                          <span>Revenue (Invoices)</span>
                          <span>{formatCurrency(pnlData.summary.total_revenue)}</span>
                        </div>
                        <div className="pl-3 space-y-1 text-muted-foreground">
                          <div className="flex justify-between"><span>Recorded Expenses</span><span className="text-red-600">({formatCurrency(pnlData.summary.recorded_expenses)})</span></div>
                          <div className="flex justify-between"><span>Bank Expenses</span><span className="text-red-600">({formatCurrency(pnlData.summary.bank_expenses)})</span></div>
                        </div>
                        <div className="flex justify-between py-2 border-t font-bold text-base">
                          <span>Net Profit</span>
                          <span className={pnlData.summary.net_profit >= 0 ? "text-green-600" : "text-red-600"}>
                            {formatCurrency(pnlData.summary.net_profit)}
                          </span>
                        </div>
                        {pnlData.expense_breakdown?.bank?.length > 0 && (
                          <div className="pt-2">
                            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Expense Breakdown</p>
                            {pnlData.expense_breakdown.bank.slice(0, 6).map(e => (
                              <div key={e.category} className="flex justify-between py-1 text-xs capitalize">
                                <span>{e.category}</span>
                                <span className="text-red-500">({formatCurrency(e.total)})</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Client Profitability Analysis</CardTitle>
                <CardDescription>Revenue and collection metrics by client</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead className="text-right">Daily Rate</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                      <TableHead className="text-right">Collection %</TableHead>
                      <TableHead className="text-right">Share %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientProfitability?.clients?.map((client) => (
                      <TableRow key={client.client}>
                        <TableCell className="font-medium">{client.client}</TableCell>
                        <TableCell className="text-right">{formatCurrency(client.daily_rate)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(client.revenue)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(client.paid)}</TableCell>
                        <TableCell className="text-right text-yellow-600">{formatCurrency(client.pending)}</TableCell>
                        <TableCell className="text-right">{client.invoice_count}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary" className={client.collection_rate >= 90 ? "bg-green-100 text-green-800" : client.collection_rate >= 70 ? "bg-yellow-100 text-yellow-800" : "bg-red-100 text-red-800"}>
                            {client.collection_rate}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">{client.revenue_share}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aging Tab */}
          <TabsContent value="aging" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Aging Summary */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Accounts Receivable Aging</CardTitle>
                  <CardDescription>As of {agingReport?.as_of_date}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Total Outstanding</p>
                    <p className="text-3xl font-bold text-primary">
                      {formatCurrency(agingReport?.total_outstanding)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {agingReport?.total_invoices} unpaid invoices
                    </p>
                  </div>
                  
                  {agingReport?.aging_buckets?.map((bucket) => (
                    <div key={bucket.bucket} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{bucket.label}</p>
                        <p className="text-xs text-muted-foreground">{bucket.count} invoices</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(bucket.amount)}</p>
                        <Badge variant="secondary" className={
                          bucket.bucket === "current" ? "bg-green-100 text-green-800" :
                          bucket.bucket === "31_60" ? "bg-yellow-100 text-yellow-800" :
                          bucket.bucket === "61_90" ? "bg-orange-100 text-orange-800" :
                          "bg-red-100 text-red-800"
                        }>
                          {bucket.percent_of_total}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Aging Chart */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Aging Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  {agingReport?.aging_buckets && (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={agingReport.aging_buckets} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
                        <YAxis dataKey="label" type="category" width={120} />
                        <Tooltip formatter={(v) => formatCurrency(v)} />
                        <Bar dataKey="amount" fill="#2E5C46">
                          {agingReport.aging_buckets.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={
                              entry.bucket === "current" ? "#2E5C46" :
                              entry.bucket === "31_60" ? "#FFB347" :
                              entry.bucket === "61_90" ? "#FF8C00" :
                              "#FF6B6B"
                            } />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Overdue Invoices Table */}
            {agingReport?.aging_buckets?.filter(b => b.bucket !== "current").some(b => b.invoices?.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    Overdue Invoices
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Days Overdue</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overdueInvoicesSorted.map((inv) => (
                          <TableRow key={inv.invoice_number || `inv-${inv.due_date}-${inv.amount}`}>
                            <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                            <TableCell>{inv.client}</TableCell>
                            <TableCell className="text-right">{formatCurrency(inv.amount)}</TableCell>
                            <TableCell>{inv.due_date}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant="secondary" className={
                                inv.days_overdue <= 60 ? "bg-yellow-100 text-yellow-800" :
                                inv.days_overdue <= 90 ? "bg-orange-100 text-orange-800" :
                                "bg-red-100 text-red-800"
                              }>
                                {inv.days_overdue} days
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{inv.status}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <ArrowUpRight className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Inflow</p>
                      <p className="text-2xl font-bold text-green-600">
                        {formatCurrency(cashFlow?.total_inflow)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                      <ArrowDownRight className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Outflow</p>
                      <p className="text-2xl font-bold text-red-600">
                        {formatCurrency(cashFlow?.total_outflow)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Net Cash Flow</p>
                      <p className={`text-2xl font-bold ${cashFlow?.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cashFlow?.net_cash_flow)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cash Flow Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {cashFlow?.by_month && (
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={cashFlow.by_month}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend />
                      <Area type="monotone" dataKey="inflow" stackId="1" stroke="#2E5C46" fill="#2E5C46" name="Inflow" />
                      <Area type="monotone" dataKey="outflow" stackId="2" stroke="#FF6B6B" fill="#FF6B6B" name="Outflow" />
                      <Line type="monotone" dataKey="running_balance" stroke="#4A8F76" strokeWidth={2} name="Running Balance" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Year-over-Year Comparison</CardTitle>
                <CardDescription>Revenue trends across {viewType === "financial" ? "financial" : "calendar"} years</CardDescription>
              </CardHeader>
              <CardContent>
                {yearComparison?.years && (
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={yearComparison.years}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend />
                      <Bar dataKey="paid" fill="#2E5C46" name="Paid" />
                      <Bar dataKey="pending" fill="#FFB347" name="Pending" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Year-over-Year Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead className="text-right">Total Invoiced</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead className="text-right">Invoices</TableHead>
                      <TableHead className="text-right">Clients</TableHead>
                      <TableHead className="text-right">Avg Invoice</TableHead>
                      <TableHead className="text-right">YoY Growth</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {yearComparison?.years?.map((year) => (
                      <TableRow key={year.year}>
                        <TableCell className="font-medium">{year.year}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(year.total_invoiced)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(year.paid)}</TableCell>
                        <TableCell className="text-right text-yellow-600">{formatCurrency(year.pending)}</TableCell>
                        <TableCell className="text-right">{year.invoice_count}</TableCell>
                        <TableCell className="text-right">{year.active_clients}</TableCell>
                        <TableCell className="text-right">{formatCurrency(year.avg_invoice)}</TableCell>
                        <TableCell className="text-right">
                          {year.yoy_growth !== 0 && (
                            <Badge variant="secondary" className={year.yoy_growth > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                              {year.yoy_growth > 0 ? '+' : ''}{year.yoy_growth}%
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
